// SPDX-License-Identifier: GPL-3.0-or-later
//
// Owns the root FileSystemDirectoryHandle: pick it, persist it across reloads
// via idb-keyval, and manage its permission lifecycle. Permission state is
// reported but never silently requested — requestPermission *must* run inside
// a user gesture.

import { get, set, del } from 'idb-keyval';

const HANDLE_KEY = 'applify:root-handle';

export type RootPermission = 'granted' | 'prompt' | 'denied';

export interface RootState {
  handle: FileSystemDirectoryHandle | null;
  permission: RootPermission | null;
}

export function supportsFileSystemAccess(): boolean {
  return typeof window !== 'undefined' && 'showDirectoryPicker' in window;
}

export async function loadStoredRoot(): Promise<FileSystemDirectoryHandle | null> {
  const stored = await get<FileSystemDirectoryHandle>(HANDLE_KEY);
  return stored ?? null;
}

export async function queryRootPermission(
  handle: FileSystemDirectoryHandle,
): Promise<RootPermission> {
  return handle.queryPermission({ mode: 'readwrite' }) as Promise<RootPermission>;
}

export async function requestRootPermission(
  handle: FileSystemDirectoryHandle,
): Promise<RootPermission> {
  return handle.requestPermission({ mode: 'readwrite' }) as Promise<RootPermission>;
}

export async function pickRoot(): Promise<FileSystemDirectoryHandle> {
  if (!supportsFileSystemAccess()) {
    throw new Error('File System Access API not supported in this browser.');
  }
  const handle = await window.showDirectoryPicker({ mode: 'readwrite' });
  await set(HANDLE_KEY, handle);
  return handle;
}

export async function clearRoot(): Promise<void> {
  await del(HANDLE_KEY);
}

export async function getRootState(): Promise<RootState> {
  const handle = await loadStoredRoot();
  if (!handle) return { handle: null, permission: null };
  const permission = await queryRootPermission(handle);
  return { handle, permission };
}
